import { departmentWeakSpots, DepartmentWeakSpot, managersLagging, ManagerLagging, departmentStages } from "@/mocks/rop";

function formatMoney(value: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

interface DepartmentAnalysisModalProps {
  onClose: () => void;
}

export default function DepartmentAnalysisModal({ onClose }: DepartmentAnalysisModalProps) {
  return (
    <div className="fixed inset-0 bg-foreground-950/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg border border-[#D5CA8A] w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 bg-[#FEF3C7] border-b border-[#D5CA8A] rounded-t-lg">
          <div className="flex items-center gap-2">
            <i className="ri-pie-chart-line text-[#D97706]"></i>
            <span className="text-[12px] font-semibold text-[#2D2D2D]">Анализ всего отдела продаж: где теряются деньги</span>
          </div>
          <button onClick={onClose} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#E8DB6C] cursor-pointer">
            <i className="ri-close-line text-[#666666]"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {/* Weak Spots */}
          <p className="text-[12px] font-semibold text-[#2D2D2D] mb-3">Слабые этапы воронки</p>
          <div className="grid grid-cols-1 gap-3 mb-4">
            {departmentWeakSpots.map((spot: DepartmentWeakSpot) => (
              <div key={spot.stage} className="p-3 border border-[#DC2626]/30 bg-[#FEE2E2]/30 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-semibold text-[#DC2626]">{spot.stage}</span>
                  <span className="text-[10px] text-[#DC2626] font-medium">Потери: {formatMoney(spot.lostRevenue)}</span>
                </div>
                <p className="text-[10px] text-[#666666] mb-1">{spot.issue}</p>
                <p className="text-[10px] text-[#2D6A4F] bg-[#D4EDDA]/50 px-2 py-1 rounded">
                  <i className="ri-lightbulb-line mr-1"></i>
                  {spot.recommendation}
                </p>
              </div>
            ))}
          </div>

          {/* Lagging Managers */}
          <p className="text-[12px] font-semibold text-[#2D2D2D] mb-3">Отстающие менеджеры</p>
          <div className="space-y-2 mb-4">
            <div className="flex items-center text-[10px] text-[#666666] px-2 py-1 bg-[#FDF2B5] rounded">
              <span className="w-[120px]">Менеджер</span>
              <span className="flex-1">Метрика</span>
              <span className="w-[80px] text-right">Значение</span>
              <span className="w-[100px] text-right">Среднее по отделу</span>
              <span className="w-[60px] text-right">Разрыв</span>
            </div>
            {managersLagging.map((m: ManagerLagging, i: number) => (
              <div key={i} className="flex items-center text-[10px] px-2 py-1.5 border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 rounded transition-colors">
                <span className="w-[120px] text-[#2D2D2D] font-medium">{m.name}</span>
                <span className="flex-1 text-[#666666]">{m.metric}</span>
                <span className="w-[80px] text-right text-[#D97706] font-medium">{m.value}</span>
                <span className="w-[100px] text-right text-[#666666]">{m.departmentAvg}</span>
                <span className="w-[60px] text-right text-[#DC2626] font-medium">{m.gap}</span>
              </div>
            ))}
          </div>

          {/* Funnel Summary */}
          <p className="text-[12px] font-semibold text-[#2D2D2D] mb-3">Воронка продаж (июнь 2026)</p>
          <div className="space-y-1">
            {departmentStages.map((stage) => {
              const maxCount = departmentStages[0].count;
              const width = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
              return (
                <div key={stage.name} className="flex items-center gap-3">
                  <div className="w-[140px] text-[10px] text-[#2D2D2D] text-right truncate">{stage.name}</div>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="h-6 bg-[#FDF2B5] rounded flex items-center" style={{ width: `${width}%` }}>
                      <span className="text-[10px] font-medium text-[#2D2D2D] px-1">{stage.count}</span>
                    </div>
                    <span className="text-[10px] text-[#666666]">{(stage.conversion * 100).toFixed(0)}%</span>
                  </div>
                  {stage.lossReason && (
                    <span className="text-[9px] text-[#DC2626] bg-[#FEE2E2] px-1.5 py-0.5 rounded truncate max-w-[120px]">{stage.lossReason}</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 p-3 bg-[#FEF3C7] rounded-lg">
            <p className="text-[11px] font-semibold text-[#2D2D2D] mb-1">Вывод AI</p>
            <p className="text-[10px] text-[#666666]">
              Отдел теряет ~{formatMoney(departmentWeakSpots.reduce((s, w) => s + w.lostRevenue, 0))} на трёх проблемных этапах.
              Ключевая точка потерь — этап презентации и КП (конверсия 55%). Петров Д.Н. отстаёт по дисциплине и конверсии КП→счёт —
              требуется индивидуальный коучинг. Рекомендуется еженедельный разбор потерянных сделок на планёрке и внедрение скриптов презентации.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#D5CA8A] bg-[#FFF9E6] rounded-b-lg">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-[11px] text-[#666666] hover:bg-[#FDF2B5] cursor-pointer transition-colors whitespace-nowrap">
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}